import { useState, useEffect, useMemo, useRef } from 'react';
import { Storage } from '../storage';
import { TIMELINE_CATEGORIES } from '../constants';
import Icon from './Icon';

// ─── TEMPLATE BROWSER ────────────────────────────────────────────────────────
//
//  Standalone template browser used inside the Dashboard create modal.
//  Props:
//    onUseTemplate  {function}  called with the newly-created timeline object
//    onClose        {function}  close the browser without selecting
//

const RECENTLY_USED_KEY = 'chronos_recently_used_templates';
const MAX_RECENT = 3;

function getRecentlyUsed() {
  try { return JSON.parse(localStorage.getItem(RECENTLY_USED_KEY) || '[]'); } catch { return []; }
}
function addRecentlyUsed(tpl) {
  const prev = getRecentlyUsed().filter(r => r.id !== tpl.id);
  localStorage.setItem(RECENTLY_USED_KEY, JSON.stringify([{ id: tpl.id, name: tpl.name, orientation: tpl.orientation, previewColor: tpl.previewColor }, ...prev].slice(0, MAX_RECENT)));
}

// ── CSS-based thumbnail ───────────────────────────────────────────────────────

const TplThumb = ({ orientation, color, size = 'md' }) => {
  const bg   = color || 'var(--accent)';
  const dim  = size === 'sm' ? { w: 56, h: 56 } : { w: '100%', h: 72 };
  const style = { width: dim.w, height: dim.h, background: `color-mix(in srgb, ${bg} 15%, var(--bg2))` };

  const barStyle = { background: bg };

  if (orientation === 'vertical' || orientation === 'zigzag' || orientation === 'spine') {
    return (
      <div className={`tpl-thumb ${orientation}`} style={style}>
        {[0,1,2].map(i => (
          <div key={i} className="tpl-thumb-bar" style={{ ...barStyle, marginLeft: orientation === 'zigzag' && i % 2 === 1 ? 20 : 0 }} />
        ))}
      </div>
    );
  }
  if (orientation === 'horizontal' || orientation === 'newspaper' || orientation === 'filmstrip') {
    return (
      <div className={`tpl-thumb horizontal`} style={style}>
        {[0,1,2].map(i => <div key={i} className="tpl-thumb-bar" style={barStyle} />)}
      </div>
    );
  }
  if (orientation === 'storyline') {
    return (
      <div className="tpl-thumb storyline" style={style}>
        {[0,1].map(g => (
          <div key={g} className="tpl-thumb-group">
            {[0,1,2].map(i => <div key={i} className="tpl-thumb-bar" style={barStyle} />)}
          </div>
        ))}
      </div>
    );
  }
  if (orientation === 'map') {
    return (
      <div className="tpl-thumb map" style={style}>
        <div className="tpl-thumb-circle" style={{ borderColor: bg }}>
          {[0,1,2].map(i => <div key={i} className="tpl-thumb-dot" style={barStyle} />)}
        </div>
      </div>
    );
  }
  if (orientation === 'swimlane') {
    return (
      <div className="tpl-thumb swimlane" style={style}>
        {[0,1,2].map(i => <div key={i} className="tpl-thumb-track" style={barStyle} />)}
      </div>
    );
  }
  if (orientation === 'tree') {
    return (
      <div className="tpl-thumb tree" style={style}>
        <div className="tpl-thumb-row"><div className="tpl-thumb-node" style={barStyle} /></div>
        <div className="tpl-thumb-row">
          {[0,1].map(i => <div key={i} className="tpl-thumb-node" style={barStyle} />)}
        </div>
        <div className="tpl-thumb-row">
          {[0,1,2,3].map(i => <div key={i} className="tpl-thumb-node" style={barStyle} />)}
        </div>
      </div>
    );
  }
  // fallback
  return (
    <div className="tpl-thumb horizontal" style={style}>
      {[0,1,2].map(i => <div key={i} className="tpl-thumb-bar" style={barStyle} />)}
    </div>
  );
};

// ── Template card ─────────────────────────────────────────────────────────────

const TplCard = ({ tpl, selected, onClick }) => (
  <button
    type="button"
    className={`tpl-card${selected ? ' selected' : ''}`}
    onClick={() => onClick(tpl)}
    aria-pressed={selected}
    aria-label={`${tpl.name} template, ${tpl.orientation} layout`}
  >
    {tpl.isFeatured && <span className="tpl-featured-badge">⭐ Featured</span>}
    <TplThumb orientation={tpl.orientation} color={tpl.previewColor} />
    <div className="tpl-card-body">
      <div className="tpl-card-name">{tpl.name}</div>
      <div className="tpl-card-badges">
        <span className="tpl-badge">{TIMELINE_CATEGORIES.find(c => c.value === tpl.category)?.label ?? tpl.category}</span>
        <span className="tpl-badge">{tpl.orientation.charAt(0).toUpperCase() + tpl.orientation.slice(1)}</span>
      </div>
      {tpl.useCount > 0 && (
        <div className="tpl-card-uses">Used {tpl.useCount.toLocaleString()} time{tpl.useCount !== 1 ? 's' : ''}</div>
      )}
    </div>
  </button>
);

// ── Detail panel ──────────────────────────────────────────────────────────────

const TplDetail = ({ tpl, fullTpl, loading, using, onUse, onClose }) => {
  const events = fullTpl?.timelineData?.events ?? [];
  const chapters = fullTpl?.timelineData?.chapters ?? [];

  return (
    <div className="tpl-detail-panel">
      <div className="tpl-detail-header">
        <div className="tpl-detail-thumb">
          <TplThumb orientation={tpl.orientation} color={tpl.previewColor} size="sm" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{tpl.name}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{tpl.description}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <span className="tpl-badge">{TIMELINE_CATEGORIES.find(c => c.value === tpl.category)?.label ?? tpl.category}</span>
            <span className="tpl-badge">{tpl.orientation.charAt(0).toUpperCase() + tpl.orientation.slice(1)}</span>
            {chapters.length > 0 && <span className="tpl-badge">{chapters.length} chapters</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: 4, flexShrink: 0 }}
        >
          ✕
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>Loading preview…</p>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            {events.length} Events
          </div>
          <ol className="tpl-detail-events">
            {events.map((ev, i) => (
              <li key={ev.id || i}>{ev.title}</li>
            ))}
          </ol>
        </>
      )}

      <button
        type="button"
        className="btn-primary tpl-use-btn"
        onClick={onUse}
        disabled={using || loading}
      >
        {using ? 'Setting up your timeline…' : 'Use This Template'}
      </button>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const TemplateBrowser = ({ onUseTemplate }) => {
  const [templates,    setTemplates]    = useState([]);
  const [loadingList,  setLoadingList]  = useState(true);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [activeCat,    setActiveCat]    = useState('');
  const [selected,     setSelected]     = useState(null);   // template card selected
  const [fullTpl,      setFullTpl]      = useState(null);   // full template data
  const [loadingFull,  setLoadingFull]  = useState(false);
  const [using,        setUsing]        = useState(false);
  const [error,        setError]        = useState('');
  const [recentlyUsed, setRecentlyUsed] = useState(getRecentlyUsed);
  const detailRef = useRef(null);

  // Load template list
  useEffect(() => {
    Storage.getTemplates()
      .then(({ templates: list }) => setTemplates(list))
      .catch(() => setError('Failed to load templates.'))
      .finally(() => setLoadingList(false));
  }, []);

  // Load full template when a card is selected
  useEffect(() => {
    if (!selected) { setFullTpl(null); return; }
    setLoadingFull(true);
    setFullTpl(null);
    Storage.getTemplate(selected.id)
      .then(data => setFullTpl(data))
      .catch(() => {})
      .finally(() => setLoadingFull(false));
    // Scroll detail panel into view
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  }, [selected?.id]);

  // Filtered templates
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return templates.filter(t => {
      if (activeCat && t.category !== activeCat) return false;
      if (q && !t.name.toLowerCase().includes(q) && !t.description.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, searchQuery, activeCat]);

  // Categories that have at least one template
  const availableCats = useMemo(() => {
    const catSet = new Set(templates.map(t => t.category));
    return TIMELINE_CATEGORIES.filter(c => catSet.has(c.value));
  }, [templates]);

  const handleUse = async () => {
    if (!selected) return;
    setUsing(true);
    setError('');
    try {
      const result = await Storage.createFromTemplate(selected.id);
      addRecentlyUsed(selected);
      setRecentlyUsed(getRecentlyUsed());
      onUseTemplate(result.timeline);
    } catch (err) {
      setError(err.message || 'Failed to create timeline from template.');
    } finally {
      setUsing(false);
    }
  };

  const handleRecentClick = (recent) => {
    const tpl = templates.find(t => t.id === recent.id) || {
      id: recent.id, name: recent.name, orientation: recent.orientation,
      previewColor: recent.previewColor, category: '', isFeatured: false, useCount: 0,
    };
    setSelected(tpl);
  };

  return (
    <div>
      {/* Error */}
      {error && (
        <div style={{ background: 'var(--danger)22', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {/* Recently used */}
      {recentlyUsed.length > 0 && !searchQuery && !activeCat && (
        <div className="tpl-recently-used">
          <div className="tpl-recently-label">Recently Used</div>
          <div className="tpl-recently-row">
            {recentlyUsed.map(r => (
              <button key={r.id} type="button" className="tpl-recent-chip" onClick={() => handleRecentClick(r)}>
                <TplThumb orientation={r.orientation} color={r.previewColor} size="sm" />
                {r.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none', display: 'flex' }}>
          <Icon name="search" size={14} />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search templates…"
          style={{ paddingLeft: 32, paddingRight: searchQuery ? 32 : 12 }}
          aria-label="Search templates"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', display: 'flex' }}
          >
            <Icon name="close" size={13} />
          </button>
        )}
      </div>

      {/* Category pills */}
      {availableCats.length > 0 && (
        <div className="tpl-cat-pills">
          <button type="button" className={`tpl-cat-pill${!activeCat ? ' active' : ''}`} onClick={() => setActiveCat('')}>
            All
          </button>
          {availableCats.map(c => (
            <button
              key={c.value}
              type="button"
              className={`tpl-cat-pill${activeCat === c.value ? ' active' : ''}`}
              onClick={() => setActiveCat(activeCat === c.value ? '' : c.value)}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      {loadingList ? (
        <div className="tpl-grid">
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', border: '1.5px solid var(--border)', animation: `pulse 1.4s ease-in-out infinite`, animationDelay: `${i * 0.1}s` }}>
              <div style={{ height: 72, background: 'var(--bg2)' }} />
              <div style={{ padding: '10px 12px 12px' }}>
                <div style={{ height: 13, background: 'var(--bg2)', borderRadius: 4, marginBottom: 8 }} />
                <div style={{ height: 10, background: 'var(--bg2)', borderRadius: 4, width: '60%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
          <p style={{ fontSize: 14 }}>No templates match your search.</p>
          <button type="button" className="btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => { setSearchQuery(''); setActiveCat(''); }}>Clear filters</button>
        </div>
      ) : (
        <div className="tpl-grid">
          {filtered.map(tpl => (
            <TplCard
              key={tpl.id}
              tpl={tpl}
              selected={selected?.id === tpl.id}
              onClick={t => setSelected(prev => prev?.id === t.id ? null : t)}
            />
          ))}
        </div>
      )}

      {/* Detail panel */}
      <div ref={detailRef}>
        {selected && (
          <TplDetail
            tpl={selected}
            fullTpl={fullTpl}
            loading={loadingFull}
            using={using}
            onUse={handleUse}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </div>
  );
};

export default TemplateBrowser;
export { TplThumb, addRecentlyUsed };
